/**
 * User-management capability projection for UI affordances.
 *
 * The backend exposes `/api/v1/auth/capabilities`, computed by the same
 * `check_admin_permission` predicate that protects user-management handlers.
 * That response is the authority for the controls below. Role and permission
 * strings are retained only for the router gate; they are never used to invent
 * a mutation capability while the backend answer is absent.
 */

import { ROLES } from '@core/constants/permissions';

/** Capabilities the user-management surface gates its affordances on. */
export type UserCapability =
  | 'create'
  | 'update'
  | 'delete'
  | 'activate'
  | 'manageRoles'
  | 'resetPassword'
  | 'importExport';

/** Exhaustive capability list used by tests and decision-table consumers. */
export const USER_CAPABILITIES: readonly UserCapability[] = [
  'create',
  'update',
  'delete',
  'activate',
  'manageRoles',
  'resetPassword',
  'importExport'
] as const;

/**
 * Platform-admin role set accepted by the backend admin router.
 *
 * The backend capability response still decides the operation after this
 * coarse route gate. Both documented spellings are retained deliberately.
 */
export const USER_ADMIN_ROLES: readonly string[] = [
  ROLES.SYSTEM_ADMIN,
  'system_admin',
  'super_admin',
  'admin'
] as const;

/** Minimum generated capability shape consumed by this feature. */
export interface BackendCapabilities {
  canViewUsers: boolean;
  canManageUsers: boolean;
}

const BACKEND_DECIDER: Readonly<Record<UserCapability, 'canManageUsers'>> = {
  create: 'canManageUsers',
  update: 'canManageUsers',
  delete: 'canManageUsers',
  activate: 'canManageUsers',
  manageRoles: 'canManageUsers',
  resetPassword: 'canManageUsers',
  importExport: 'canManageUsers'
};

/**
 * Resolve a mutation affordance from the backend-computed capability.
 *
 * Unknown/unavailable capability state fails closed. Local permission strings
 * are intentionally ignored because they use several historical dialects and
 * are not the predicate enforced by the backend handlers.
 */
export function hasUserCapability(
  capability: UserCapability,
  roles: readonly string[] | null | undefined,
  _permissions: readonly string[] | null | undefined,
  backend?: BackendCapabilities | null
): boolean {
  if (!roles?.some(role => USER_ADMIN_ROLES.includes(role))) {
    return false;
  }
  return backend?.[BACKEND_DECIDER[capability]] === true;
}
